import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link2Off, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/site/Logo";
import { adminInput } from "@/components/admin/shared";

const INVALID = "This invite link is no longer valid";

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [invalid, setInvalid] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/auth/invite/${token}`).then((r) => setInfo(r.data)).catch(() => setInvalid(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (pw.length < 8) errs.pw = "Password must be at least 8 characters";
    if (pw !== pw2) errs.pw2 = "Passwords don't match";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/invite/accept", { token, password: pw });
      setUser(data.user);
      navigate("/portal", { replace: true });
    } catch (err) {
      if (err.response?.status === 410) setInvalid(true);
      else setErrors({ form: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="invite-page" className="noise relative flex min-h-screen items-center justify-center overflow-hidden bg-[#07090E] px-4 py-12">
      <div className="bg-grid grid-fade absolute inset-0" />
      <div className="rise relative w-full max-w-md rounded-2xl border border-white/10 bg-[#0D111A]/85 p-6 backdrop-blur-xl sm:p-10">
        <Logo />
        {invalid ? (
          <div data-testid="invite-invalid-message" className="mt-8 text-center">
            <Link2Off className="mx-auto h-8 w-8 text-amber-300" />
            <h1 className="mt-4 text-xl font-bold text-white">{INVALID}</h1>
            <p className="mt-2 text-sm text-slate-400">Ask Beall Digital Group to resend your invite, or sign in if you already set a password.</p>
            <a href="/login" data-testid="invite-login-link" className="mt-6 inline-block text-sm text-cyan-300 hover:text-white">Go to sign in →</a>
          </div>
        ) : !info ? (
          <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin text-cyan-400" />
        ) : (
          <>
            <h1 className="mt-8 text-2xl font-bold tracking-tight text-white">Welcome, {info.name.split(" ")[0]}</h1>
            <p className="mt-2 text-sm text-slate-400">Create a password for <span data-testid="invite-email" className="text-slate-200">{info.email}</span> to open your client portal.</p>
            <form onSubmit={submit} noValidate className="mt-8 space-y-4">
              <div>
                <label htmlFor="invite-pw" className="mb-1.5 block text-sm text-slate-300">Password</label>
                <input id="invite-pw" type="password" autoComplete="new-password" data-testid="invite-input-password" value={pw} onChange={(e) => setPw(e.target.value)} className={adminInput(errors.pw)} />
                {errors.pw && <p data-testid="invite-password-error" className="mt-1 text-xs text-red-400">{errors.pw}</p>}
              </div>
              <div>
                <label htmlFor="invite-pw2" className="mb-1.5 block text-sm text-slate-300">Confirm password</label>
                <input id="invite-pw2" type="password" autoComplete="new-password" data-testid="invite-input-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} className={adminInput(errors.pw2)} />
                {errors.pw2 && <p data-testid="invite-confirm-error" className="mt-1 text-xs text-red-400">{errors.pw2}</p>}
              </div>
              {errors.form && <p className="text-sm text-red-400">{errors.form}</p>}
              <button type="submit" disabled={busy} data-testid="invite-submit-button" className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-5 py-3 text-sm font-semibold text-[#07090E] hover:bg-cyan-300 disabled:opacity-60">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set password & open portal
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
